import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Routes } from '../../config/routes';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';
import { ViolationStatement } from '../../models/entities/violation-statement';
import { ViolationHeaderFooter } from 'app/models/entities/violation-header-footer';
import { ViolationTemplate } from 'app/models/entities/violation-template';
import { ViolationStatementComment } from 'app/models/entities/violation-statement-comment';
import { UploadedFile } from 'app/models/entities/uploaded-file';
import { HttpHeaders } from '@angular/common/http';
import { ViolationHistory } from '../../models/entities/violation-history';
import { ViolationStatus } from '../../models/entities/violation-status';
import { Violations } from 'app/components/shared/http/request-interfaces';

@Injectable({
    providedIn: 'root',
})
export class ViolationsService
{
    private _http: ApplicationHttpClient;

    constructor(_http: ApplicationHttpClient) {
        this._http = _http;
    }

    /**
     * Return Violation detail
     * @param {number} violationId
     * @returns {Observable<ViolationStatement>}
     */
    public getViolation(violationId: number): Observable<ViolationStatement> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Violation(violationId),
        );

        return this._http.get(url).pipe(map((response: Object) => {
            return new ViolationStatement(response);
        }));
    }

    /**
     * Get the permitted members and admins who have access to violations for this org.
     */
    public getOrgViolationAccessors(ifShowAllMembers: number): Observable<any[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrganizationViolationAccessors(jwt.orgId) + '/' + ifShowAllMembers,
        );

        return this._http.get(url);
    }

    /**
     * Create Violation
     * @param {Object} requestBody
     * @returns {Observable<ViolationStatement[]>}
     */
    public createViolations(
            requestBody: Violations.CreateViolationStatementRequest,
    ): Observable<ViolationStatement[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http
                .post(
                        Routes.MakeLegFiCoreUrl(
                                Routes.LegFiCore.OrganizationViolations(jwt.orgId),
                        ),
                        JSON.stringify(requestBody),
                ).pipe(
                        map((response: Object[]) => {
                            return response.map(violation => {
                                return new ViolationStatement(violation);
                            });
                        }));
    }

    /**
     * Update the Violation
     * @param {number} violationId
     * @param {Object} requestBody
     * @returns {Observable<ViolationStatement>}
     */
    public updateViolation(
            violationId: number,
            requestBody: Violations.UpdateViolationStatementRequest,
    ): Observable<ViolationStatement> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http
                .patch(
                        Routes.MakeLegFiCoreUrl(
                                Routes.LegFiCore.Violation(violationId),
                        ),
                        JSON.stringify(requestBody),
                ).pipe(
                        map((response: Object) => {
                            return new ViolationStatement(response);
                        }));
    }

    /**
     * Mark a set of 1 or more violation as completed, or pending.
     *
     * @param violationIds
     * @param isMarkComplete
     * @param emailOwner
     * @param organizationId
     */
    toggleCompleteOrPending(
            violationIds: number[],
            isMarkComplete: number,
            emailOwner: boolean,
            organizationId?: number,
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const orgId = organizationId || jwt.orgId;
        const url = Routes.MakeLegFiCoreUrl(
                `${Routes.LegFiCore.OrganizationViolations(orgId)}/completion/${isMarkComplete}`,
        );
        return this._http.patch(url, JSON.stringify({violationIds: violationIds, emailOwner: emailOwner}));
    }

    /**
     * Unit(s) just got a violation fine added; go save the history.
     *
     * @param fines
     */
    saveViolationFineHistory(fines: Violations.RecordViolationFineRequest): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.UnitViolationFines(jwt.orgId),
        );

        return this._http.post(url, JSON.stringify({fines: fines}));
    }


    /**
     * Change violation status to any of the org's violation status ids.
     *
     * @param violationId
     * @param request
     */
    changeViolationStatus(request: Violations.ChangeViolationStatusRequest): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrganizationViolations(jwt.orgId) + `/status`,
        );
        return this._http.patch(url, JSON.stringify(request));
    }

    /**
     * Delete the Violation
     * @param {number[]} violationIds
     * @param {number} organizationId
     * @returns {Observable<ViolationStatement>}
     */
    public deleteViolation(
            violationIds: number[],
            organizationId?: number,
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const orgId = organizationId || jwt.orgId;
        const url = Routes.MakeLegFiCoreUrl(
                `${Routes.LegFiCore.OrganizationViolations(orgId)}/delete`,
        );
        return this._http.patch(url, JSON.stringify({violationIds: violationIds}));
    }

    /**
     * Return list of all violations owned by the specified organization.
     * @param {number} organizationId
     * @returns {Observable<ViolationStatement[]>}
     */
    public getOrganizationViolations(
            organizationId: number,
            isExport: boolean = false,
    ): Observable<ViolationStatement[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrganizationViolations(organizationId || jwt.orgId, isExport),
        );

        return this._http.get(url).pipe(map((response: any) => {
            const violations = response.violations;
            const units = response.ref.units;
            return violations.map(violation => {
                return new ViolationStatement(violation, units[violation.unitId]);
            });
        }));
    }

    /**
     * Generate PDF of the inquiry list
     * @returns {Observable<Object>}
     */
    public getViolationListPdf(violationIds: number[]): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrganizationViolations(jwt.orgId) + '/pdf',
        );

        const options: Object = {
            responseType: 'blob',
        };

        return this._http.post(url, JSON.stringify({violationIds: violationIds}), options);
    }

    /**
     * Generate PDF of the inquiry list
     * @returns {Observable<Object>}
     */
    public getUnitViolationListPdf(unitId: number, status: string): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.UnitViolations(jwt.orgId) + `/pdf?status=${status}` + (unitId
                        ? `&unitIds[]=${unitId}`
                        : ''),
        );

        const newHeaders = new HttpHeaders({
            'Content-Type': 'application/pdf',
        });

        const options: Object = {
            headers: newHeaders,
            responseType: 'arraybuffer',
        };

        return this._http.get(url, options);
    }

    /**
     * Get the history of one violation.
     *
     * @param violationId
     */
    public getViolationHistory(violationId: number): Observable<ViolationHistory[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrganizationViolations(jwt.orgId) + '/' + violationId + '/history',
        );

        return this._http.get(url).pipe(map((violations: any) => {
            return violations.map(violation => {
                return new ViolationHistory(violation);
            });
        }));
    }

    /**
     * Return list of graph info for violations
     * @param filters
     */
    public getOrgViolationsForGraph(
            filters: Violations.ViolationGraphRequest,
    ): Observable<ViolationStatement[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrganizationViolationGraph(
                        jwt.orgId,
                ) + query,
        );

        return this._http.get(url);
    }

    /**
     * Return list of graph info for violations
     * @param filters
     */
    public getOrgViolationPastDueCounts(): Observable<number[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrgViolationPastDueCounts(
                        jwt.orgId,
                ),
        );

        return this._http.get(url);
    }

    /**
     * Return list of all unit violations
     * @param {number} orgId
     * @param {number} unitId
     * @returns {Observable<ViolationStatement[]>}
     */
    public getUnitViolations(
            orgId: number,
            unitId: number = null,
    ): Observable<ViolationStatement[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UnitViolations(orgId || jwt.orgId)) + (unitId
                ? `?unitIds[]=${unitId}`
                : '');

        return this._http.get(url).pipe(map((response: Object[]) => {
            return response.map(violation => {
                return new ViolationStatement(violation);
            });
        }));
    }

    public notifyViolationStatement(
            requestId: number,
            requestBody: {
                memberIds: number[];
                organizationId?: number;
            },
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(`${Routes.LegFiCore.Violation(requestId)}/users/notify`);
        return this._http.post(url, JSON.stringify(requestBody));
    }

    public sendViolationStatementEmails(
            requestBody: object,
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.post(
                Routes.MakeLegFiCoreUrl(
                        Routes.LegFiCore.ViolationEmails(jwt.orgId),
                ),
                JSON.stringify(requestBody),
        );
    }


    /**
     * @param {Object} requestBody
     */
    public updateResolveDateInBulk(
            requestBody: Violations.ChangeViolationResolveByRequest,
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.patch(
                Routes.MakeLegFiCoreUrl(
                        Routes.LegFiCore.ViolationResolution(jwt.orgId),
                ),
                JSON.stringify(requestBody),
        );
    }

    /**
     * Create Request without Attachments
     * @param {Object} requestBody
     * @returns {Observable<MaintenanceRequest[]>}
     */

    /**
     * Generate PDF of Violation
     * @param {number} organizationId
     * @param {number} violationId
     * @returns {Observable<Object>}
     */
    public generateViolationPdf(
            organizationId: number,
            violationId: number,
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Organization(organizationId || jwt.orgId) +
                '/violations/' +
                violationId +
                '/pdf',
        );

        const newHeaders = new HttpHeaders({
            'Content-Type': 'application/pdf',
        });

        const options: Object = {
            headers: newHeaders,
            responseType: 'arraybuffer',
        };

        return this._http.get(url, options);
    }

    public loadAttachments(
            violationId: number,
    ): Observable<UploadedFile[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return ApplicationHttpClient.instance.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.LinkedFiles('violation', violationId),
        );

        return this._http.get(url).pipe(map((response: Object[]) => {
            return response.map(attachment => {
                return new UploadedFile(attachment);
            });
        }));
    }

    public getComments(
            violationId: number,
            filters: { [key: string]: any } = {},
    ): Observable<ViolationStatementComment[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.ViolationComments(violationId) + query);

        return this._http.get(url).pipe(map((response: Object[]) => {
            return response.map(comment => {
                return new ViolationStatementComment(comment);
            });
        }));
    }

    public createComment(
            requestId: number,
            requestBody: Violations.CreateViolationCommentRequest,
    ): Observable<ViolationStatementComment> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.ViolationComments(requestId));

        return this._http.post(url, JSON.stringify(requestBody)).pipe(
                map((response: Object) => new ViolationStatementComment(response)),
        );
    }

    public updateComment(
            requestId: number,
            commentId: number,
            requestBody: Violations.UpdateViolationCommentRequest,
            filters: { [key: string]: any } = {},
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.ViolationComments(requestId) + '/' + commentId + query);

        return this._http.patch(url, JSON.stringify(requestBody));
    }

    public deleteComment(
            requestId: number,
            commentId: number,
            filters: { [key: string]: any } = {},
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const query = this._http.queryStringFromObject(filters);
        const url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.ViolationComments(requestId) + '/' + commentId + query);
        return this._http.delete(url);
    }

    public getViolationTemplates(orgId?: number): Observable<ViolationTemplate[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ViolationTemplates(orgId || jwt.orgId),
        );

        return this._http.get(url).pipe(map((response: Object[]) => {
            return response.map(template => {
                return new ViolationTemplate(template);
            });
        }));
    }

    public getViolationHeaderFooters(
            orgId?: number,
    ): Observable<ViolationHeaderFooter[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ViolationHeaderFooter(orgId || jwt.orgId),
        );

        return this._http.get(url).pipe(map((response: Object[]) => {
            return response.map(template => {
                return new ViolationHeaderFooter(template);
            });
        }));
    }

    public getViolationStatuses(orgId = null): Observable<ViolationStatus[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ViolationStatus(orgId ?? jwt.orgId),
        );

        return this._http.get(url).pipe(map((response: Object[]) => {
            return response.map(template => {
                return new ViolationStatus(template);
            });
        }));
    }

    // TODO: replace async/promise with observable above
    public async getViolationStatusesPromise(orgId = null): Promise<ViolationStatus[]> {
        return this.getViolationStatuses(orgId).toPromise();
    }

    public createOrUpdateViolationHeaderFooter(
            orgId: number,
            templateId: number,
            body: Violations.ViolationTemplateRequest,
    ): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ViolationHeaderFooter(orgId || jwt.orgId) + '/' + templateId,
        );

        return this._http
                .post(url, JSON.stringify(body)).pipe(
                        map((response: Object[]) => {
                            return response.map(template => {
                                return new ViolationHeaderFooter(template);
                            });
                        }));
    }

    /** Set or clear if this template id is set as default for an org */
    public toggleHeaderFooterDefaultStatus(
            templateId: number,
            isDefault: number,
    ): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ViolationHeaderFooter(jwt.orgId) + '/' + templateId + '/default/' + isDefault,
        );

        return this._http
                .patch(url, JSON.stringify({})).pipe(
                        map((response: Object[]) => {
                            return response.map(template => {
                                return new ViolationHeaderFooter(template);
                            });
                        }));
    }

    /**
     * Delete a header/footer template
     *
     * @param templateId
     */
    public deleteHeaderFooterTemplate(templateId: number) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ViolationHeaderFooter(jwt.orgId) + '/' + templateId,
        );

        return this._http.delete(url).pipe(
                map((response: Object[]) => {
                    return response.map(template => {
                        return new ViolationHeaderFooter(template);
                    });
                }));
    }

    /**
     * Delete a title/message template
     *
     * @param templateId
     */
    public deleteViolationTemplate(templateId: number) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ViolationTemplates(jwt.orgId) + '/' + templateId,
        );

        return this._http.delete(url);
    }

    /**
     * Create a violation status
     *
     * @param body
     */
    public createViolationStatus(body: Violations.ViolationStatusRequest) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ViolationStatus(jwt.orgId) + '/0',
        );

        return this._http.patch(url, JSON.stringify(body));
    }

    /**
     * Edit a violation status
     *
     * @param body
     */
    public editViolationStatus(statusId: number, body: Violations.ViolationStatusRequest) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ViolationStatus(jwt.orgId) + '/' + statusId,
        );

        return this._http.patch(url, JSON.stringify(body));
    }

    /**
     * Send all org violations with new sort order
     *
     * @param body
     */
    public updateStatusSortOrder(body: ViolationStatus[]) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ViolationStatus(jwt.orgId) + '/sort',
        );

        return this._http.patch(url, JSON.stringify({statuses: body}));
    }

    /**
     * Delete a violation status
     *
     * @param statusId
     */
    public deleteViolationStatus(statusId: number) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ViolationStatus(jwt.orgId) + '/' + statusId,
        );

        return this._http.delete(url);
    }

    /**
     * Create or Edit the title/message template
     *
     * @param templateId
     * @param data
     */
    public createOrUpdateViolationTemplate(templateId: number, data: Violations.ViolationTemplateRequest) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.ViolationTemplates(jwt.orgId) + '/' + templateId,
        );

        return this._http.post(url, JSON.stringify(data)).pipe(
                map((response: Object[]) => {
                    return response.map(template => {
                        return new ViolationTemplate(template);
                    });
                }));
    }

    public getViolationSettings(organizationId: number): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrganizationViolations(organizationId || jwt.orgId, false) + '/settings',
        );

        return this._http.get(url);
    }

    public updateViolationSettings(organizationId: number, ids: number[]): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const memberIds = ids.join(',');
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrganizationViolations(organizationId || jwt.orgId, false) + '/settings',
        );

        return this._http.post(url, JSON.stringify({memberIds: memberIds}));
    }

    /** Helper to get the specific violation status from the given org list and specific status title. */
    getViolationStatusFromOrgList(status: string, orgStatuses: ViolationStatus[]): ViolationStatus {
        return orgStatuses.filter(s => s.title === status) ? orgStatuses.filter(s => s.title === status)[0] : null;
    }
}

